import { Controller } from "@hotwired/stimulus"

// FD-696: collapse a portfolio board column down to a narrow spine.
//
// Every Initiative status gets a column whether or not anything is in it, so a small
// workspace used to render five 288px columns of which three were blank — the board was
// mostly empty space and the columns that mattered were pushed off-screen. Empty columns
// now render collapsed (the server sets the initial value); any column can be folded or
// unfolded by hand, and the choice is remembered per workspace + status for the tab.
//
// A collapsed column is NOT a dead end for drag and drop. Its body — the [data-kanban-column]
// element that IS the SortableJS drop zone — is display:none while collapsed, so dragging a
// card over the spine expands the column first and puts the real drop zone back on screen
// mid-drag. Without that, folding a column away would quietly make that status unreachable.
//
// sessionStorage (not localStorage) and the try/catch around it mirror
// sidebar_scroll_controller: a layout preference is worth remembering for the session and
// worth nothing at all if private mode or a quota error takes the page down with it.
export default class extends Controller {
  static targets = ["header", "body", "spine", "spineCount"]
  static values = { collapsed: Boolean, storageKey: String }

  connect() {
    // A remembered choice beats the server's "empty ⇒ collapsed" default; with no stored
    // choice the server default stands. Assigning the value re-runs the render below.
    const stored = this.#read()
    if (stored === null) this.#render()
    else this.collapsedValue = stored === "1"
  }

  toggle() {
    this.collapsedValue = !this.collapsedValue
    this.#write()
  }

  // Used by the spine's click AND by dragover, so it must be idempotent and cheap —
  // dragover fires continuously while a card hovers the spine.
  expand() {
    if (!this.collapsedValue) return
    this.collapsedValue = false
    this.#write()
  }

  collapsedValueChanged() {
    this.#render()
  }

  #render() {
    const collapsed = this.collapsedValue
    this.element.classList.toggle("w-72", !collapsed)
    this.element.classList.toggle("w-11", collapsed)
    if (this.hasHeaderTarget) this.headerTarget.classList.toggle("hidden", collapsed)
    if (this.hasBodyTarget) this.bodyTarget.classList.toggle("hidden", collapsed)
    if (this.hasSpineTarget) this.spineTarget.classList.toggle("hidden", !collapsed)
    // The spine is only on screen while collapsed, and the only way a card enters or leaves
    // a column is a drag — which expands it first — so recounting here is enough to keep the
    // spine's badge honest without listening for anything.
    if (collapsed && this.hasSpineCountTarget && this.hasBodyTarget) {
      this.spineCountTarget.textContent = this.bodyTarget.querySelectorAll("[data-item-url]").length
    }
  }

  #read() {
    if (!this.storageKeyValue) return null
    try {
      return sessionStorage.getItem(this.storageKeyValue)
    } catch {
      return null
    }
  }

  #write() {
    if (!this.storageKeyValue) return
    try {
      sessionStorage.setItem(this.storageKeyValue, this.collapsedValue ? "1" : "0")
    } catch {
      // Remembering the layout is a nicety; never let a storage failure break the board.
    }
  }
}
